import { inject } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import { GLOBAL_STORE_INJECTION_KEY } from "@/store/keys";
import { loadOnboardingStatusGuard, redirectToProductGuard } from "./guards";
import { AppPages } from './pages';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      beforeEnter: [ loadOnboardingStatusGuard ],
      name: AppPages.Default,
      path: "/",
      redirect: () => ({ name: AppPages.Onboarding }),
    },
    {
      component: () => import("@/views/Auth/Login.vue"),
      name: AppPages.Login,
      path: "/login",
    },
    {
      component: () => import("@/views/Auth/Logout.vue"),
      name: AppPages.Logout,
      path: "/logout",
    },
    {
      component: () => import("@/views/Auth/Signup.vue"),
      name: AppPages.Signup,
      path: "/signup",
    },
    {
      component: () => import("@/views/Auth/Callback.vue"),
      name: AppPages.AuthCallback,
      path: "/callback",
    },
    {
      component: () => import("@/views/Auth/Error.vue"),
      name: AppPages.AuthError,
      path: "/auth-error",
    },
    {
      beforeEnter: [ loadOnboardingStatusGuard ],
      children: [
        {
          component: () => import("@/views/Onboarding/Business/BusinessDetails.vue"),
          name: AppPages.OnboardingBusinessDetails,
          path: "business-details",
        },
        {
          component: () => import("@/views/Onboarding/BusinessOwnersList.vue"),
          name: AppPages.OnboardingBusinessOwners,
          path: "business-owners",
        },
        {
          component: () => import("@/views/Onboarding/Documents/AgreementSigning.vue"),
          name: AppPages.OnboardingAgreementSigning,
          path: "agreement-signing",
        },
        {
          component: () => import("@/views/Onboarding/StatusInReview.vue"),
          name: AppPages.OnboardingStatusInReview,
          path: "status/in-review",
        },
        {
          component: () => import("@/views/Onboarding/StatusRejected.vue"),
          name: AppPages.OnboardingStatusRejected,
          path: "status/rejected",
        },
        {
          component: () => import("@/views/Onboarding/StatusAccepted.vue"),
          name: AppPages.OnboardingStatusApproved,
          path: "status/approved",
        },
        {
          beforeEnter: [ redirectToProductGuard ],
          component: () => import("@/views/Onboarding/StatusAccepted.vue"),
          name: AppPages.RedirectToProduct,
          path: 'completed',
        },
      ],
      component: () => import("@/views/Onboarding/Onboarding.vue"),
      name: AppPages.Onboarding,
      path: "/onboarding",
    },
    {
      component: () => import("@/views/NotFound/index.vue"),
      name: "not-found",
      path: "/not-found",
    },
    {
      component: () => import("@/views/Onboarding/OwnerAdditionalDetails/index.vue"),
      name: "owner-verification",
      path: "/owner-verification",
    },
    {
      component: () => import('@/views/Signup/index.vue'),
      meta: {
        requiresAuth: true,
        title: 'Signup',
      },
      name: 'signup',
      path: '/signup',
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: () => ({ name: AppPages.Default, params: {} }),
    },
  ],
});

router.beforeEach(() => {
  const globalStore = inject(GLOBAL_STORE_INJECTION_KEY)!;
  globalStore.state.isRouteLoading = true;
});

router.afterEach(() => {
  const globalStore = inject(GLOBAL_STORE_INJECTION_KEY)!;
  globalStore.state.isRouteLoading = false;
});

// If user gets a "Chunk loading error", reload once
router.onError((error) => {
  const chunkFailed = error.message?.includes("Failed to fetch dynamically imported module") ||
    error.message?.includes("Importing a module script failed") ||
    error.message?.includes("Unable to preload CSS");
  if (chunkFailed) {
     window.location.reload(); 
  }
});

export default router;
